import { createAppSlice } from "../../app/createAppSlice"
import { createEntityAdapter, isAnyOf } from '@reduxjs/toolkit'
import { fetchReleaseNotesAPI, updateReleaseNotesAPI } from "./releaseNotesAPI"
import { startAppListening } from "../../app/listenerMiddleware"

//entity: { id: "sequential", toolname: "", releasenumber: "", releasedate: "YYYY-MM-DD", dateDisplay: "Month D, YYYY", releasedescription: ""}
//key uses: to provide release notes for all tools and platforms produced by Computable Publishing

const releaseNotesAdapter = createEntityAdapter({
    sortComparer: (a, b) => {
        if (b?.releasedate && a?.releasedate && b.releasedate !== a.releasedate) {
            return b.releasedate.localeCompare(a.releasedate)
        } else if (b?.toolname && a?.toolname && b.toolname !== a.toolname) {
            return b.toolname.localeCompare(a.toolname)
        } else if (b?.releasenumber && a?.releasenumber && b.releasenumber !== a.releasenumber) {
            return b.releasenumber.localeCompare(a.releasenumber)
        } else {
            return 1 //0 would be preferred if order doesn't matter
        }
    }
})

const initialState = releaseNotesAdapter.getInitialState({
    status: 'idle',
    error: null
})

const releaseNotesSlice = createAppSlice({
    name: "releaseNotes",
    initialState,
    reducers: create => ({
        releaseNoteAdded: create.preparedReducer(
            (releaseNoteBody) => {
                return {
                    payload: {
                        id: releaseNoteBody.id, 
                        toolname: releaseNoteBody.toolname, 
                        releasenumber: releaseNoteBody.releasenumber, 
                        releasedate: releaseNoteBody.releasedate, 
                        dateDisplay: releaseNoteBody.dateDisplay, 
                        releasedescription: releaseNoteBody.releasedescription
                    }
                }
            },
            releaseNotesAdapter.addOne
        ),
        releaseNoteUpdated: create.reducer((state, action) => {
            const { id, toolname, releasenumber, releasedate, dateDisplay, releasedescription} = action.payload;
            releaseNotesAdapter.updateOne(state, 
                { id, changes: { toolname, releasenumber, releasedate, dateDisplay, releasedescription } })
        }),
        fetchReleaseNotes: create.asyncThunk(
            async () => {
                const response = await fetchReleaseNotesAPI()
                return response.data
            },
            {
                options: {
                    condition(arg, thunkApi) {
                        const { releaseNotes } = thunkApi.getState()
                        if (releaseNotes.status !== 'idle') {
                            return false
                        }
                    }
                },
                pending: state => {
                    state.status = "loading"
                },
                fulfilled: (state, action) => {
                    state.status = "succeeded"
                    releaseNotesAdapter.setAll(state, action.payload)
                },
                rejected: (state, action) => {
                    state.status = "failed"
                    state.error = action.error.message ?? "Unknown Error"
                },
            },
        ),
    }),
    selectors: {
        selectReleaseNotesStatus: releaseNotes => releaseNotes.status,
        selectReleaseNotesError: releaseNotes => releaseNotes.error
    },
})

export const { releaseNoteAdded, releaseNoteUpdated, fetchReleaseNotes } = releaseNotesSlice.actions

export const { selectReleaseNotesStatus, selectReleaseNotesError } = releaseNotesSlice.selectors

export const {
    selectAll: selectAllReleaseNotes,
    selectById: selectReleaseNoteById,
} = releaseNotesAdapter.getSelectors(state => state.releaseNotes)

export const addReleaseNotesListeners = () => {

    startAppListening({
        matcher: isAnyOf(releaseNoteAdded, releaseNoteUpdated),
        effect: async (action) => {
            console.log("releaseNotes listener was triggered")
            try {
                const newReleaseNote = action.payload;
                let response = await updateReleaseNotesAPI(newReleaseNote);
                if (response?.success) {
                    alert("update successful")
                } else {
                    alert("FAILED")
                }
            } catch (err) {
                console.log("FAILURE of updateReleaseNotes: ", err)
            }
        }
    })
}

export default releaseNotesSlice
